import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import Lenis from "lenis";
import "@/App.css";
import ErrorBoundary from "@/components/ErrorBoundary";
import EntryGate from "@/components/EntryGate";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { ScrollToTop, ProtectedRoute, AdminRoute, WhatsAppFloat } from "@/components/guards";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { LanguageProvider, useLang } from "@/lib/i18n";
import { MUSIC_PRIMARY, MUSIC_FALLBACK } from "@/lib/api";

import Home from "@/pages/Home";
import Winkel from "@/pages/Winkel";
import ProductDetail from "@/pages/ProductDetail";
import Winkelwagen from "@/pages/Winkelwagen";
import Checkout from "@/pages/Checkout";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Inloggen from "@/pages/Inloggen";
import Registreren from "@/pages/Registreren";
import WachtwoordVergeten from "@/pages/WachtwoordVergeten";
import WachtwoordResetten from "@/pages/WachtwoordResetten";
import AuthCallback from "@/pages/AuthCallback";
import Account from "@/pages/Account";
import AccountOrders from "@/pages/AccountOrders";
import AccountOrderDetail from "@/pages/AccountOrderDetail";
import AdminLayout from "@/pages/AdminLayout";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminProducts from "@/pages/AdminProducts";
import AdminOrders from "@/pages/AdminOrders";
import { AdminBrands, AdminCoupons, AdminCustomers, AdminReviews, AdminReturns, AdminMessages } from "@/pages/AdminMisc";
import Contact from "@/pages/Contact";
import Faq from "@/pages/Faq";
import Verzending from "@/pages/Verzending";
import Retourneren from "@/pages/Retourneren";

const NotFound = () => {
  const { t } = useLang();
  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center px-6 pt-24 text-center">
      <h1 className="font-serif font-light text-cream text-5xl">{t("notfound.title")}</h1>
      <p className="mt-4 text-sm text-mist">{t("notfound.sub")}</p>
      <Link data-testid="notfound-home" to="/" className="mt-8 bg-gold text-obsidian font-mono text-[10px] tracking-[0.3em] uppercase px-8 py-4 hover:bg-gold-light transition-colors">{t("notfound.home")}</Link>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <LanguageProvider>
        <AuthProvider>
          <CartProvider>
            <AppShell />
          </CartProvider>
        </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

function AppShell() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const [entered, setEntered] = useState(() => sessionStorage.getItem("deo_entered") === "1");
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);
  const lenisRef = useRef(null);

  useEffect(() => {
    if (!entered) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, [entered]);

  const startExperience = () => {
    sessionStorage.setItem("deo_entered", "1");
    setEntered(true);
    if (!audioRef.current) {
      const audio = new Audio(MUSIC_PRIMARY);
      audio.loop = true;
      audio.volume = 0.35;
      audio.onerror = () => {
        audio.src = MUSIC_FALLBACK;
        audio.play().catch(() => {});
      };
      audio.play().catch(() => {});
      audioRef.current = audio;
    }
  };

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      if (audioRef.current) audioRef.current.muted = next;
      return next;
    });
  };

  return (
    <>
      <AnimatePresence>{!entered && !isAdmin && <EntryGate key="gate" onEnter={startExperience} />}</AnimatePresence>

      <div className="bg-obsidian text-cream min-h-screen">
        {!isAdmin && <div className="grain-overlay" />}
        {!isAdmin && <Nav muted={muted} onToggleMute={toggleMute} />}

        <main>
          <Routes>
            <Route path="/" element={<Home entered={entered} />} />
            <Route path="/winkel" element={<Winkel />} />
            <Route path="/product/:slug" element={<ProductDetail />} />
            <Route path="/winkelwagen" element={<Winkelwagen />} />
            <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/inloggen" element={<Inloggen />} />
            <Route path="/registreren" element={<Registreren />} />
            <Route path="/wachtwoord-vergeten" element={<WachtwoordVergeten />} />
            <Route path="/wachtwoord-resetten" element={<WachtwoordResetten />} />
            <Route path="/auth/google" element={<AuthCallback />} />
            <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
            <Route path="/account/bestellingen" element={<ProtectedRoute><AccountOrders /></ProtectedRoute>} />
            <Route path="/account/bestellingen/:orderId" element={<ProtectedRoute><AccountOrderDetail /></ProtectedRoute>} />
            <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="producten" element={<AdminProducts />} />
              <Route path="merken" element={<div className="grid xl:grid-cols-2 gap-10"><AdminBrands /><AdminCoupons /></div>} />
              <Route path="bestellingen" element={<AdminOrders />} />
              <Route path="klanten" element={<AdminCustomers />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="retouren" element={<AdminReturns />} />
              <Route path="berichten" element={<AdminMessages />} />
            </Route>
            <Route path="/contact" element={<Contact />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/verzending" element={<Verzending />} />
            <Route path="/retourneren" element={<Retourneren />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>

        {!isAdmin && <Footer />}
        {!isAdmin && <WhatsAppFloat />}
      </div>
    </>
  );
}

export default App;
